/**
 * Copyright 2016 The AMP HTML Authors. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS-IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { bytesToString, stringToBytes, utf8Decode } from './bytes';

/**
 * Character mapping from base64url to base64.
 * @const {!Object<string, string>}
 */
const base64UrlDecodeSubs: Record<string, string> = { '-': '+', _: '/', '.': '=' };

/**
 * Character mapping from base64 to base64url.
 * @const {!Object<string, string>}
 */
const base64UrlEncodeSubs: Record<string, string> = { '+': '-', '/': '_', '=': '.' };

/**
 * Converts a string which is in base64 encoding into a Uint8Array
 * containing the decoded value.
 */
export function base64DecodeToBytes(str: string): Uint8Array {
  return stringToBytes(atob(str));
}

/**
 * Converts a string which is in base64url encoding into a Uint8Array
 * containing the decoded value.
 */
export function base64UrlDecodeToBytes(str: string): Uint8Array {
  const encoded = atob(str.replace(/[-_.]/g, (ch) => base64UrlDecodeSubs[ch]));
  return stringToBytes(encoded);
}

/**
 * Converts a bytes array into base64url encoded string.
 * base64url is defined in RFC 4648. It is sometimes referred to as "web safe".
 */
export function base64UrlEncodeFromBytes(bytes: Uint8Array): string {
  const str = bytesToString(bytes);
  return btoa(str).replace(/[+/=]/g, (ch) => base64UrlEncodeSubs[ch]);
}

/**
 * Decode a base64url encoded string by `base64UrlEncodeFromString`
 */
export function base64UrlDecodeFromString(str: string): string {
  const bytes = base64UrlDecodeToBytes(str);
  return utf8Decode(bytes);
}

/**
 * Converts a bytes array into base64 encoded string.
 */
export function base64EncodeFromBytes(bytes: Uint8Array): string {
  return btoa(bytesToString(bytes));
}
